import { Point as PointIcon } from "@logora/debate.icons.regular_icons";
import AvTimerIcon from "@mui/icons-material/AvTimer";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PersonIcon from "@mui/icons-material/Person";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { getTranslatedContent } from "@logora/debate.translation.translated_content";
import React, { useState, useEffect, useCallback } from "react";
import {
	Link,
	useDataProvider,
	useGetIdentity,
	useTranslate,
} from "react-admin";
import { flags } from "../../debate/translation_entries/TranslationList";
import FullNameField from "../../inputs/FullNameField";
import { providerIcons } from "../../social_networks/oauth_providers/icons";
import dayjs from "../../utils/dayJsSetup";
import classes from "./ModerableElement.module.scss";
import { ModerationReport } from "./ModerationReport";

const ModerableElement = ({
	parentId = null,
	moderable = null,
	moderableType,
	moderableScore,
	hideInfos = false,
	showStatus = false,
	submitReformulation,
}) => {
	const [entry, setEntry] = useState(moderable || null);
	const [showParent, setShowParent] = useState(false);
	const [showReports, setShowReports] = useState(false);
	const [parents, setParents] = useState([]);
	const [isReformulating, setIsReformulating] = useState(false);
	const [showReformulatedContent, setShowReformulatedContent] = useState(false);
	const { identity } = useGetIdentity();
	const dataProvider = useDataProvider();
	const translate = useTranslate();
	const allowReformulation = identity?.settings?.translation?.enable;

	const getAuthor = (entry) => {
		switch (moderableType) {
			case "Group":
				return entry?.debate_suggestion?.author;
			case "User":
				return entry;
			default:
				return entry.author;
		}
	};

	const showModerableContent = (entry) => {
		if (entry) {
			switch (moderableType) {
				case "Message":
					return entry.content;
				case "Proposal":
					return entry.content;
				case "Group":
					return entry.name;
				case "User":
					return entry.description;
				default:
					return entry.content;
			}
		}
	};

	const [reformulatedValue, setReformulatedValue] = useState(
		entry?.reformulated_content || showModerableContent(entry),
	);

	const fetchElement = useCallback(async () => {
		try {
			const response = await dataProvider.getOne("messages", {
				id: parentId,
				with_parents: true,
			});
			const entry = response.data;
			const parents = response.data.parents;
			if (entry) {
				setEntry(entry);
			}
			if (parents && parents.length > 0) {
				setParents(parents);
			}
		} catch (error) {
			console.log(error);
		}
	}, [dataProvider, parentId]);

	const updateReformulatedContent = async (value) => {
		if (reformulatedValue !== entry.reformulated_content) {
			const updatedTable =
				moderableType === "Message"
					? "messages"
					: moderableType === "Group"
						? "groups"
						: "";
			try {
				const response = await dataProvider.update(updatedTable, {
					id: entry.id,
					data: {
						reformulated_content: value,
					},
				});
			} catch (error) {
				console.log(error);
			}
		}
	};

	const showModerableType = (entry) => {
		switch (moderableType) {
			case "Message":
				if (entry.is_reply) {
					return translate("resources.moderation_entries.type.reply");
				}
				if (entry.group_type === "Source") {
					return translate("resources.moderation_entries.type.comment");
				}
				return translate("resources.moderation_entries.type.message");
			case "Proposal":
				return translate("resources.moderation_entries.type.proposal");
			case "Group":
				return translate("resources.moderation_entries.type.debate_suggestion");
			case "User":
				return translate("resources.moderation_entries.type.user");
			default:
				return null;
		}
	};

	const showModerableOrigin = (entry) => {
		switch (moderableType) {
			case "Message":
				const groupTitle = getTranslatedContent(
					entry.group?.title || entry.group?.name,
					entry.group?.language,
					entry.group_type === "Source" ? "title" : "name",
					entry.group?.translation_entries,
					entry.language
				);
				if (entry.group_type === "Source") {
					return entry.group.source_url ? (
						<a
							className={classes.originLink}
							href={entry.group?.source_url}
							target="_blank"
							rel={"noopener noreferrer"}
							style={{
								textDecoration: "none",
								color: "rgb(91, 127, 241)",
							}}
						>
							{groupTitle}
						</a>
					) : (
						groupTitle
					);
				}
				return (
					<a
						className={classes.originLink}
						href={entry?.direct_url}
						target="_blank"
						rel={"noopener noreferrer"}
						style={{
							textDecoration: "none",
							color: "rgb(91, 127, 241)",
						}}
					>
						{groupTitle}
					</a>
				);
			case "Proposal":
				const consultationTitle = getTranslatedContent(
					entry.consultation?.title,
					entry.consultation?.language,
					"title",
					entry.consultation?.translation_entries,
					entry.language
				);
				return (
					<a
						className={classes.originLink}
						href={entry?.direct_url}
						target="_blank"
						rel={"noopener noreferrer"}
						style={{
							textDecoration: "none",
							color: "rgb(91, 127, 241)",
						}}
					>
						{consultationTitle}
					</a>
				);
			default:
				return null;
		}
	};

	const showAttachment = (entry) => {
		if (!entry.attachment_type) return null;

		switch (entry.attachment_type) {
			case "image":
				return (
					<img
						src={entry.attachment_url}
						alt={entry.attachment_url}
						style={{
							maxWidth: 200,
							height: "auto",
						}}
					/>
				);
			case "video":
				return (
					<video width="200" height="auto" controls>
						<source src={entry.attachment_url} type="video/mp4" />
						<track
							kind="captions"
							src={entry.attachment_url.replace(".mp4", ".vtt")}
						/>
					</video>
				);
			case "shared_link":
				return <Link to={entry.attachment_url}>{entry.attachment_url}</Link>;
			default:
				return null;
		}
	};

	const handleReformulate = (value) => {
		setReformulatedValue(value);
	};

	const handleSubmitReformulation = (value) => {
		if (allowReformulation) {
			updateReformulatedContent(value);
			submitReformulation(value);
			setShowReformulatedContent(true);
			setIsReformulating(false);
		}
	};

	useEffect(() => {
		setShowReformulatedContent(Boolean(moderable?.reformulated_content)); // Reset show status
		setReformulatedValue(
			moderable?.reformulated_content || showModerableContent(moderable),
		); // Reset reformulated_content default value
		if (parentId && moderable == null) {
			fetchElement();
		} else if (moderable !== null) {
			setEntry(moderable);
		}
	}, [moderable, fetchElement, parentId]);

	return (
		entry && (
			<>
				{entry?.moderation_entry?.moderable_type !==
					"SocialModerator::ModerableContent" &&
					!hideInfos && (
						<div className={classes.entryHeader}>
							<div className={classes.entryType}>
								{showModerableType(entry)}
							</div>
							{showModerableOrigin(entry)}
						</div>
					)}
				{parents &&
					parents.length > 0 &&
					parents
						.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
						.map((elm) => (
							<ModerableElement
								key={elm.id}
								moderable={elm}
								moderableType={"Message"}
								hideInfos
							/>
						))}
				{allowReformulation &&
					entry.reformulated_content &&
					showModerableContent(entry) !== entry.reformulated_content && (
						<Box display={"flex"} gap={".5em"} marginTop={"1em"}>
							<Switch
								onChange={() =>
									setShowReformulatedContent(!showReformulatedContent)
								}
								checked={showReformulatedContent}
								size="small"
								label="Contenu original"
							/>
							<span>Contenu reformulé</span>
						</Box>
					)}
				<Paper className={classes.content} elevation={1}>
					<div className={classes.contentHeader}>
						{entry?.moderation_entry?.moderable_type ===
							"SocialModerator::ModerableContent" ? (
							<Stack
								direction="row"
								alignItems="center"
								gap={1}
								sx={{
									textTransform: "capitalize",
									marginBottom: "15px",
								}}
							>
								<img
									src={providerIcons[entry?.moderable_source.provider]}
									alt=""
									width={30}
								/>
								{entry?.author_name || `${entry?.provider} User`}
							</Stack>
						) : (
							<Link
								to={`/users/${moderableType === "User"
									? entry.hash_id
									: getAuthor(entry)?.hash_id
									}`}
							>
								<div
									className={classes.user}
									style={{
										display: "flex",
										flexWrap: "nowrap",
										alignItems: "center",
										color: "rgb(91, 127, 241)",
										cursor: "pointer",
									}}
								>
									<FullNameField user={getAuthor(entry)} />
								</div>
							</Link>
						)}
						<>
							<span className={classes.createdAt}>
								{entry.edited_at ?
									`${translate("resources.messages.fields.edited_at")}: ${dayjs(entry.edited_at).format("DD-MM-YYYY HH:mm")}`
									:
									dayjs(entry.created_at).format("DD-MM-YYYY HH:mm")
								}
							</span>
							{entry?.position && (
								<div className={classes.position}>
									{getTranslatedContent(
										entry.position.name,
										entry.position.language,
										"name",
										entry.position?.translation_entries,
										entry.language
									)}

								</div>
							)}
						</>
					</div>
					{moderableType === "Proposal" && (
						<b className={classes.moderableTitle}>{entry.title}</b>
					)}
					<Box sx={{ position: "relative" }}>
						{isReformulating ? (
							<TextField
								onChange={(e) => handleReformulate(e.target.value)}
								value={reformulatedValue}
								variant="outlined"
								multiline
								fullWidth
							/>
						) : (
							<>
								<div
									className={classes.moderableContent}
									dangerouslySetInnerHTML={{
										__html: showReformulatedContent
											? entry?.reformulated_content &&
											entry?.reformulated_content
											: showModerableContent(entry),
									}}
								/>

								{moderableType === "SocialModerator::ModerableContent" &&
									showAttachment(entry)}
							</>
						)}
						<Box display={"flex"} gap={"1em"} marginTop={"1em"}>
							{moderableType === "SocialModerator::ModerableContent" && (
								<a
									className={classes.originAbsoluteLink}
									target="_blank"
									rel={"noopener noreferrer"}
									href={entry?.url || entry?.direct_url}
								>
									{translate(
										"resources.moderation_entries.label.see_moderable_content",
										{ platform: entry?.provider },
									)}
								</a>
							)}

							{allowReformulation &&
								!hideInfos &&
								(moderableType === "Message" || moderableType === "Group") && (
									<div
										className={classes.originAbsoluteLink}
										onClick={() => setIsReformulating(true)}
									>
										{translate(
											"resources.moderation_entries.label.reformulate",
										)}
									</div>
								)}
							{isReformulating && (
								<div
									className={`${classes.entryStatItem} ${classes.reformulateBtn}`}
									onClick={() => handleSubmitReformulation(reformulatedValue)}
								>
									Valider
								</div>
							)}
						</Box>
					</Box>
					{entry?.sources && (
						<div className={classes.subtitle}>
							{entry?.sources?.map((source) => (
								<a
									key={source.id}
									className={classes.sourcesContainer}
									href={source.source_url}
									target="_blank"
									rel="noreferrer"
								>
									{source.title}
								</a>
							))}
						</div>
					)}
					{entry?.upvotes > 0 ? (
						<>
							<div className={classes.footer}>
								<div>
									{translate("resources.moderation_entries.label.upvotes")}
								</div>
								<span>{entry.upvotes}</span>
							</div>
						</>
					) : null}
					{showStatus && moderableType === "Message" && (
						<div className={classes.statusHeader}>
							<div className={classes.status}>
								{entry.status === "rejected"
									? translate("resources.messages.status.rejected")
									: entry.status === "accepted"
										? translate("resources.messages.status.accepted")
										: translate("resources.messages.status.pending")}
							</div>
						</div>
					)}
				</Paper>
				{parentId &&
					entry.translation_entries.length > 0 &&
					entry.translation_entries.map((e, i) => (
						<Paper
							key={`entry-${e.id}`}
							sx={{ marginLeft: "25px !important" }}
							className={classes.content}
							elevation={1}
						>
							<span
								className={classes.translatedTitle}
							>{`${e.target_language.toUpperCase()} ${flags[e.target_language]
								}`}</span>
							<div
								dangerouslySetInnerHTML={{
									__html: e.translated_content,
								}}
							/>
						</Paper>
					))}
				{!hideInfos && (
					<div className={classes.entryStats}>
						{moderableScore > 0 && (
							<Tooltip
								title={translate(
									"resources.moderation_entries.label.moderation_score",
								)}
							>
								<div className={classes.entryStatItem}>
									{Math.floor(100 - moderableScore)} % <AvTimerIcon />
								</div>
							</Tooltip>
						)}
						{getAuthor(entry)?.moderation_score > 0 && (
							<Tooltip
								title={translate(
									"resources.moderation_entries.label.user_score",
								)}
							>
								<div className={classes.entryStatItem}>
									{getAuthor(entry)?.moderation_score} % <PersonIcon />
								</div>
							</Tooltip>
						)}
						{entry.score > 0 && (
							<Tooltip
								title={translate(
									"resources.moderation_entries.label.relevance_score",
								)}
							>
								<div className={classes.entryStatItem}>
									{Math.floor(entry.score)}
									<PointIcon className={classes.pointIcon} />
								</div>
							</Tooltip>
						)}
						{entry.reports?.length > 0 && (
							<>
								<Tooltip
									title={translate(
										"resources.moderation_entries.label.reports",
										{ count: entry.reports.length },
									)}
								>
									<div
										onClick={() => setShowReports(true)}
										className={classes.reportItem}
									>
										{translate(
											"resources.moderation_entries.label.show_reports",
											{
												smart_count: entry.reports.length,
											},
										)}{" "}
										<ErrorOutlineIcon />
									</div>
								</Tooltip>
								<Drawer
									anchor={"right"}
									open={showReports}
									onClose={() => setShowReports(false)}
								>
									<div className={classes.drawerTitle}>
										{translate("resources.reports.name", {
											smart_count: 2,
										})}
									</div>
									{entry.reports.map((report) => (
										// biome-ignore lint/correctness/useJsxKeyInIterable: <explanation>
										<Box
											sx={{
												width: "500px",
												padding: "15px",
											}}
										>
											<ModerationReport report={report} />
										</Box>
									))}
								</Drawer>
							</>
						)}
						{entry?.reply_to_id && (
							<React.Fragment key={"left"}>
								<Tooltip
									title={translate("resources.reports.labels.see_message")}
								>
									<div
										onClick={() => setShowParent(true)}
										className={classes.showParent}
									>
										{translate("resources.moderation_entries.label.parent")}
									</div>
								</Tooltip>
								<Drawer
									anchor={"right"}
									open={showParent}
									onClose={() => setShowParent(false)}
								>
									<div className={classes.drawerTitle}>
										{translate(
											"resources.moderation_entries.label.parent_argument",
										)}
									</div>
									<Box
										sx={{
											width: "500px",
											padding: "15px",
										}}
									>
										<ModerableElement
											parentId={entry.reply_to_id}
											moderableType={moderableType}
											hideInfos
										/>
									</Box>
								</Drawer>
							</React.Fragment>
						)}
					</div>
				)}
			</>
		)
	);
};

export default ModerableElement;
